// =============================================================================
// app/src/lib/aplicacoes.ts — Serginho Autopeças
// A tabela de aplicação do fornecedor, colada, virando linhas de fitment.
//
// Pedido do dono (11/08/2026): o catálogo da Skypeças mostra, por peça, uma
// tabela "Montadora | Veículo | Mês/Ano | Modelo | Motor | Comp | Vál. | Cil. |
// Combustível | Complemento" com uma linha por veículo. Digitar isso no
// cadastro, linha a linha, é o motivo de a aba Aplicação ficar vazia — e
// aplicação vazia é busca por veículo que não acha a peça.
//
// O CONTRATO: entra o texto colado (Ctrl+C na tabela do site → Ctrl+V aqui),
// sai o formato do Repetidor da aba Aplicação — que continua 100% editável.
// O parser é deliberadamente tolerante e NUNCA lança: linha que não deu para
// entender vira contagem em `ignoradas`, não erro. Quem confere é o olho de
// quem colou, com as linhas na tela; a pior falha possível é uma linha a menos
// para digitar à mão.
//
// Colar tabela de site traz separador de verdade (tab) porque navegador copia
// célula a célula. O fallback por 2+ espaços cobre PDF e WhatsApp, onde o tab
// vira espaçamento — aí célula VAZIA colapsa e o posicional se perde; por isso
// o fallback exige as duas primeiras colunas (montadora e veículo), que nunca
// vêm vazias nesses catálogos.
// =============================================================================

/** O shape do Repetidor da aba Aplicação (Cadastro.tsx) — strings, como lá. */
export interface AplicacaoColada {
  montadora: string;
  modelo: string;
  ano_inicial: string;
  ano_final: string;
  motorizacao: string;
  observacao: string;
}

export interface ResultadoColagem {
  aplicacoes: AplicacaoColada[];
  /** Linhas que não deu para entender (poucas colunas, sem montadora/veículo). */
  ignoradas: number;
}

// A ordem da tabela da Skypeças, usada quando o texto colado não traz cabeçalho.
const ORDEM_PADRAO = [
  'montadora', 'veiculo', 'ano', 'modelo', 'motor', 'comp', 'val', 'cil',
  'combustivel', 'complemento',
] as const;

type Coluna = (typeof ORDEM_PADRAO)[number] | null;

/** minúsculo, sem acento e sem pontuação — 'Vál.' e 'val' caem no mesmo lugar. */
function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9/ ]/g, '')
    .trim();
}

function reconhecerColuna(celula: string): Coluna {
  const c = normalizar(celula);
  if (c.includes('montadora') || c.includes('fabricante')) return 'montadora';
  if (c.includes('veiculo') || c === 'carro') return 'veiculo';
  if (c.includes('ano')) return 'ano'; // cobre 'Mês/Ano', 'Ano', 'Ano modelo'
  if (c.includes('modelo') || c.includes('versao')) return 'modelo';
  if (c.includes('motor')) return 'motor';
  // 'complemento' ANTES de 'comp': os dois começam igual, e o prefixo pegaria
  // a coluna errada.
  if (c.includes('complemento') || c.includes('obs')) return 'complemento';
  if (c.includes('combustivel')) return 'combustivel';
  if (c.startsWith('comp')) return 'comp';
  if (c.startsWith('val')) return 'val';
  if (c.startsWith('cil')) return 'cil';
  return null;
}

/** É a linha de cabeçalho da tabela? (some do resultado, vira o mapa de colunas) */
function ehCabecalho(celulas: string[]): boolean {
  const reconhecidas = celulas.filter((c) => reconhecerColuna(c) !== null).length;
  // Duas já bastam: "Montadora | Veículo" sozinho identifica um cabeçalho, e
  // exigir mais recusaria tabelas mais magras que a da Skypeças.
  return reconhecidas >= 2 && celulas.some((c) => {
    const alvo = reconhecerColuna(c);
    return alvo === 'montadora' || alvo === 'veiculo';
  });
}

function dividir(linha: string): string[] {
  // Tab quando houver (cópia de tabela de site preserva célula vazia); senão
  // 2+ espaços. Um espaço só NÃO separa: "PALIO WEEKEND" é uma célula.
  const celulas = linha.includes('\t') ? linha.split('\t') : linha.split(/\s{2,}/);
  return celulas.map((c) => c.trim());
}

/** "01/2002 até 12/2010" → ['2002','2010']; "2000 até ..." → ['2000','']. */
function extrairAnos(celula: string): [string, string] {
  const anos = celula.match(/(?:19|20)\d{2}/g) ?? [];
  return [anos[0] ?? '', anos[1] ?? ''];
}

export function colarAplicacoes(texto: string): ResultadoColagem {
  const linhas = texto
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  let mapa: Coluna[] | null = null;
  const aplicacoes: AplicacaoColada[] = [];
  const vistas = new Set<string>();
  let ignoradas = 0;

  for (const linha of linhas) {
    const celulas = dividir(linha);

    if (ehCabecalho(celulas)) {
      mapa = celulas.map(reconhecerColuna);
      continue;
    }

    if (celulas.filter(Boolean).length < 2) { ignoradas += 1; continue; }

    const ordem: readonly Coluna[] = mapa ?? ORDEM_PADRAO;
    const valor = (coluna: Coluna): string => {
      const i = ordem.indexOf(coluna);
      return i >= 0 ? (celulas[i] ?? '').trim() : '';
    };

    const montadora = valor('montadora');
    const veiculo = valor('veiculo');
    if (!montadora || !veiculo || /\d/.test(montadora)) { ignoradas += 1; continue; }

    const [anoInicial, anoFinal] = extrairAnos(valor('ano'));
    const variante = valor('modelo');
    const valvulas = valor('val').replace(/\D/g, '');
    // "1.3 FIRE 16V" — o formato que já se digita à mão no campo motorização.
    // Cilindros ficam de fora: "4 cil" em cabo de carro de passeio é ruído.
    const motorizacao = [valor('motor'), valor('comp'), valvulas ? `${valvulas}V` : '']
      .filter(Boolean)
      .join(' ');
    const observacao = [valor('combustivel'), valor('complemento')]
      .filter(Boolean)
      .join(' · ');

    const aplicacao: AplicacaoColada = {
      montadora,
      // "PALIO" + variante "WEEKEND" = modelo "PALIO WEEKEND": a coluna Modelo
      // desses catálogos é a variação do veículo, não o modelo inteiro.
      modelo: variante ? `${veiculo} ${variante}` : veiculo,
      ano_inicial: anoInicial,
      ano_final: anoFinal,
      motorizacao,
      observacao,
    };

    // A mesma linha duas vezes (colagem dupla, tabela paginada repetida) conta
    // como uma. Linhas que diferem em QUALQUER campo (1.0 vs 1.3) ficam ambas.
    const chave = JSON.stringify(aplicacao);
    if (vistas.has(chave)) continue;
    vistas.add(chave);
    aplicacoes.push(aplicacao);
  }

  return { aplicacoes, ignoradas };
}
